import styled from "styled-components";
import { Paper, Card as MuiCard } from "@mui/material";
import { styled as MuiStyled } from "@mui/material/styles";

export const Container = styled.div`
  padding: 20px;
  margin-top: 65px;
  min-height: calc(100vh - 65px);
  background-color: #f8f9fa;
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

export const Title = styled.h1`
  color: #2c3e50;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
`;

export const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #ff8038;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background-color: #fd954f;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

export const DashboardSection = styled.div`
  margin-bottom: 30px;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

export const StatCard = MuiStyled(Paper)`
  padding: 24px;
  border-radius: 12px;
  transition: all 0.3s ease;
  border-left: 4px solid;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

export const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
`;

export const StatLabel = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const StatSubtext = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

export const FiltersSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 15px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  min-width: 300px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ff8038;
  }

  &::placeholder {
    color: #999;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

export const FilterButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

interface FilterButtonProps {
  active?: boolean;
}

export const FilterButton = styled.button<FilterButtonProps>`
  padding: 8px 16px;
  border: 2px solid ${({ active }) => (active ? "#ff8038" : "#e1e5e9")};
  background-color: ${({ active }) => (active ? "#ff8038" : "white")};
  color: ${({ active }) => (active ? "white" : "#666")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    border-color: #ff8038;
    background-color: ${({ active }) => (active ? "#fd954f" : "#fff5f0")};
    color: ${({ active }) => (active ? "white" : "#ff8038")};
  }
`;

export const ComputerListSection = styled.div`
  margin-bottom: 30px;
`;

export const ComputerTable = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 120px 150px 120px 150px;
  gap: 16px;
  padding: 16px 20px;
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 120px 100px 120px;
    gap: 12px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 80px 100px;
    gap: 8px;
    padding: 12px 16px;
  }
`;

export const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 120px 150px 120px 150px;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 120px 100px 120px;
    gap: 12px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 80px 100px;
    gap: 8px;
    padding: 12px 16px;
  }
`;

export const ComputerName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

export const LastCheckTime = styled.div`
  font-size: 13px;
  color: #666;
`;

export const ElapsedTime = styled.div`
  font-size: 13px;
  color: #666;
`;

interface StatusBadgeProps {
  status: "healthy" | "stale" | "inactive";
}

export const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ status }) => {
    switch (status) {
      case "healthy":
        return `
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case "stale":
        return `
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
      case "inactive":
        return `
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      default:
        return `
          background-color: #e2e3e5;
          color: #383d41;
          border: 1px solid #d6d8db;
        `;
    }
  }}
`;

export const StatusDot = styled.div<StatusBadgeProps>`
  width: 8px;
  height: 8px;
  border-radius: 50%;

  ${({ status }) => {
    switch (status) {
      case "healthy":
        return "background-color: #28a745;";
      case "stale":
        return "background-color: #ffc107;";
      case "inactive":
        return "background-color: #dc3545;";
      default:
        return "background-color: #6c757d;";
    }
  }}
`;

export const ActionsButton = styled.button`
  padding: 6px 12px;
  background-color: transparent;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  color: #6c757d;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-color: #ff8038;
    color: #ff8038;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

export const EmptyStateTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 8px;
`;

export const EmptyStateText = styled.p`
  color: #666;
  margin-bottom: 20px;
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 20px;
`;

export const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${({ active }) => (active ? "#ff8038" : "#dee2e6")};
  background-color: ${({ active }) => (active ? "#ff8038" : "white")};
  color: ${({ active }) => (active ? "white" : "#6c757d")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #ff8038;
    background-color: ${({ active }) => (active ? "#fd954f" : "#fff5f0")};
    color: ${({ active }) => (active ? "white" : "#ff8038")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const ModalContent = MuiStyled(MuiCard)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
  border-radius: 12px;
  outline: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
`;

export const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`;

export const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 4px;

  &:hover {
    color: #2c3e50;
  }
`;

export const HiddenOnMobile = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

export const MobileOnly = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
  }
`;
